/**
 * Redux slice for the recognition (kudos) feature.
 *
 * Holds two pieces of state:
 *   - `snapshot` — the current viewer's per-company karma + giveBalance + level.
 *     Refreshed on company change and after a successful give().
 *   - `feed` — paginated list of company-wide PointTransactions, hydrated with
 *     fromUser / toUser refs. Newest-first; we prepend on real-time
 *     `kudos-given` events so the feed updates without polling.
 */
import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchMySnapshot, fetchFeed, giveKudos } from "services/points.service";
import { PointsSnapshot, PointsTransaction } from "types/Points";

interface RecognitionState {
  snapshot: PointsSnapshot | null;
  snapshotCompanyId: string | null;
  loadingSnapshot: boolean;

  feed: PointsTransaction[];
  feedCompanyId: string | null;
  feedFilters: RecognitionFeedFilters;
  feedHasMore: boolean;
  feedNextCursor: number | null;
  loadingFeed: boolean;
  loadingMore: boolean;

  giving: boolean;

  // companyId → number of un-seen kudos events. Persisted via localStorage so
  // it survives reloads and shows the right badge even before the recognition
  // socket has had a chance to deliver any events.
  unreadByCompany: Record<string, number>;
  // companyId → number of un-seen kudos where the receiver is the current user.
  // Lets the menu draw an extra bolt indicator so "personal" kudos stand out
  // from generic feed activity.
  unreadMineByCompany: Record<string, number>;
}

interface RecognitionFeedFilters {
  search?: string;
  type?: string;
  amount?: string | number;
}

const UNREAD_STORAGE_KEY = "recognitionUnreadByCompany";
const UNREAD_MINE_STORAGE_KEY = "recognitionUnreadMineByCompany";

function readMap(key: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      const out: Record<string, number> = {};
      Object.keys(parsed).forEach((k) => {
        const v = Number(parsed[k]);
        if (Number.isFinite(v) && v > 0) out[k] = v;
      });
      return out;
    }
  } catch {
    /* ignore */
  }
  return {};
}

function writeMap(key: string, state: Record<string, number>) {
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

function normalizeFeedFilters(
  filters: RecognitionFeedFilters = {}
): RecognitionFeedFilters {
  const out: RecognitionFeedFilters = {};
  const search = filters.search?.trim();
  if (search) out.search = search;
  if (filters.type && filters.type !== "all") out.type = filters.type;
  if (
    filters.amount !== undefined &&
    filters.amount !== "" &&
    filters.amount !== "all"
  ) {
    out.amount = filters.amount;
  }
  return out;
}

function transactionMatchesFeedFilters(
  transaction: PointsTransaction,
  filters: RecognitionFeedFilters,
  selfId?: string
): boolean {
  if (
    filters.search &&
    ![transaction.fromUser.name, transaction.toUser.name, transaction.reason]
      .join(" ")
      .toLowerCase()
      .includes(filters.search.toLowerCase())
  ) {
    return false;
  }

  if (
    filters.type === "sent" &&
    (!selfId || String(transaction.fromUser.id) !== String(selfId))
  ) {
    return false;
  }

  if (
    filters.type === "received" &&
    (!selfId || String(transaction.toUser.id) !== String(selfId))
  ) {
    return false;
  }

  if (filters.amount !== undefined) {
    return Number(transaction.amount) === Number(filters.amount);
  }

  return true;
}

const initialState: RecognitionState = {
  snapshot: null,
  snapshotCompanyId: null,
  loadingSnapshot: false,
  feed: [],
  feedCompanyId: null,
  feedFilters: {},
  feedHasMore: false,
  feedNextCursor: null,
  loadingFeed: false,
  loadingMore: false,
  giving: false,
  unreadByCompany: readMap(UNREAD_STORAGE_KEY),
  unreadMineByCompany: readMap(UNREAD_MINE_STORAGE_KEY),
};

export const fetchSnapshotThunk = createAsyncThunk(
  "recognition/fetchSnapshot",
  async (companyId: string) => {
    const snap = await fetchMySnapshot(companyId);
    return { companyId, snapshot: snap };
  }
);

export const fetchFeedThunk = createAsyncThunk(
  "recognition/fetchFeed",
  async (
    arg:
      | string
      | {
          companyId: string;
          search?: string;
          type?: string;
          amount?: string | number;
        }
  ) => {
    const companyId = typeof arg === "string" ? arg : arg.companyId;
    const options =
      typeof arg === "string"
        ? {}
        : normalizeFeedFilters({
            search: arg.search,
            type: arg.type,
            amount: arg.amount,
          });
    const page = await fetchFeed(companyId, options);
    return { companyId, page, filters: options };
  }
);

export const fetchMoreFeedThunk = createAsyncThunk(
  "recognition/fetchMoreFeed",
  async (
    {
      companyId,
      cursor,
      search,
      type,
      amount,
    }: {
      companyId: string;
      cursor: number;
      search?: string;
      type?: string;
      amount?: string | number;
    },
    { rejectWithValue }
  ) => {
    try {
      const page = await fetchFeed(companyId, { cursor, search, type, amount });
      return { companyId, page };
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const giveKudosThunk = createAsyncThunk(
  "recognition/give",
  async (
    {
      companyId,
      toUserId,
      reason,
      amount = 1,
    }: { companyId: string; toUserId: string; reason: string; amount?: number },
    { rejectWithValue }
  ) => {
    try {
      const r = await giveKudos(companyId, toUserId, reason, amount);
      return { companyId, ...r };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || { error: "error" });
    }
  }
);

const recognitionSlice = createSlice({
  name: "recognition",
  initialState,
  reducers: {
    /**
     * `kudos-given` socket event for the company we're currently looking at:
     * prepend the new entry into the feed and bump the receiver's karma in
     * the local snapshot if it's about us.
     *
     * Also bumps the menu-badge unread count for the company — but only when
     * the current user is NOT the sender (the sender knows they just gave a
     * kudos, no need to nag them). Recognition.tsx clears this on view.
     */
    applyKudosGiven(
      state,
      action: PayloadAction<{
        companyId: string;
        transaction: PointsTransaction;
        selfId?: string;
      }>
    ) {
      const { companyId, transaction, selfId } = action.payload;
      if (state.feedCompanyId === companyId) {
        // dedupe — server-roundtrip echoes might race with thunk fulfilment
        if (
          transactionMatchesFeedFilters(
            transaction,
            state.feedFilters,
            selfId
          ) &&
          !state.feed.find((tx) => tx.id === transaction.id)
        ) {
          state.feed = [transaction, ...state.feed];
        }
      }
      // If we're the receiver and on this company, bump local karma + level
      if (
        selfId &&
        state.snapshot &&
        state.snapshotCompanyId === companyId &&
        transaction.toUser.id === selfId
      ) {
        state.snapshot.karma += transaction.amount;
      }
      // Bump the menu badge — skip when self is the sender.
      if (!selfId || transaction.fromUser.id !== selfId) {
        state.unreadByCompany[companyId] =
          (state.unreadByCompany[companyId] || 0) + 1;
        writeMap(UNREAD_STORAGE_KEY, state.unreadByCompany);
        // If the receiver is us, also bump the "for me" counter so the menu
        // can draw an extra bolt indicator.
        if (selfId && transaction.toUser.id === selfId) {
          state.unreadMineByCompany[companyId] =
            (state.unreadMineByCompany[companyId] || 0) + 1;
          writeMap(UNREAD_MINE_STORAGE_KEY, state.unreadMineByCompany);
        }
      }
    },
    /**
     * Reset the unread badge for a company. Called by the recognition page
     * when the user is actively viewing it.
     */
    markRecognitionRead(state, action: PayloadAction<string>) {
      const companyId = action.payload;
      if (state.unreadByCompany[companyId]) {
        delete state.unreadByCompany[companyId];
        writeMap(UNREAD_STORAGE_KEY, state.unreadByCompany);
      }
      if (state.unreadMineByCompany[companyId]) {
        delete state.unreadMineByCompany[companyId];
        writeMap(UNREAD_MINE_STORAGE_KEY, state.unreadMineByCompany);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSnapshotThunk.pending, (state) => {
        state.loadingSnapshot = true;
      })
      .addCase(fetchSnapshotThunk.fulfilled, (state, action) => {
        state.loadingSnapshot = false;
        state.snapshot = action.payload.snapshot;
        state.snapshotCompanyId = action.payload.companyId;
      })
      .addCase(fetchSnapshotThunk.rejected, (state) => {
        state.loadingSnapshot = false;
      })
      .addCase(fetchFeedThunk.pending, (state, action) => {
        state.loadingFeed = true;
        state.feed = [];
        state.feedFilters =
          typeof action.meta.arg === "string"
            ? {}
            : normalizeFeedFilters({
                search: action.meta.arg.search,
                type: action.meta.arg.type,
                amount: action.meta.arg.amount,
              });
      })
      .addCase(fetchFeedThunk.fulfilled, (state, action) => {
        state.loadingFeed = false;
        state.feed = action.payload.page.entries;
        state.feedCompanyId = action.payload.companyId;
        state.feedFilters = action.payload.filters;
        state.feedHasMore = action.payload.page.hasMore;
        state.feedNextCursor = action.payload.page.nextCursor;
      })
      .addCase(fetchFeedThunk.rejected, (state) => {
        state.loadingFeed = false;
      })
      .addCase(fetchMoreFeedThunk.pending, (state) => {
        state.loadingMore = true;
      })
      .addCase(fetchMoreFeedThunk.fulfilled, (state, action) => {
        state.loadingMore = false;
        if (state.feedCompanyId === action.payload.companyId) {
          // dedupe by id in case of overlap
          const existingIds = new Set(state.feed.map((tx) => tx.id));
          const fresh = action.payload.page.entries.filter(
            (tx) => !existingIds.has(tx.id)
          );
          state.feed = [...state.feed, ...fresh];
          state.feedHasMore = action.payload.page.hasMore;
          state.feedNextCursor = action.payload.page.nextCursor;
        }
      })
      .addCase(fetchMoreFeedThunk.rejected, (state) => {
        state.loadingMore = false;
      })
      .addCase(giveKudosThunk.pending, (state) => {
        state.giving = true;
      })
      .addCase(giveKudosThunk.fulfilled, (state, action) => {
        state.giving = false;
        // Update self snapshot from server echo
        if (state.snapshotCompanyId === action.payload.companyId) {
          state.snapshot = action.payload.snapshot;
        }
        // Prepend into feed (the server's broadcast might also arrive — slice
        // dedupes by id in applyKudosGiven, so the result is correct either way)
        if (state.feedCompanyId === action.payload.companyId) {
          if (
            transactionMatchesFeedFilters(
              action.payload.transaction,
              state.feedFilters,
              action.payload.transaction.fromUser.id
            ) &&
            !state.feed.find((tx) => tx.id === action.payload.transaction.id)
          ) {
            state.feed = [action.payload.transaction, ...state.feed];
          }
        }
      })
      .addCase(giveKudosThunk.rejected, (state) => {
        state.giving = false;
      });
  },
});

export const { applyKudosGiven, markRecognitionRead } =
  recognitionSlice.actions;
export default recognitionSlice.reducer;
